/**
 * File: ThreeModel.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v1.0.0
 * Created: 2025-08-28
 * Last Updated: 2025-11-06
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

/**
 * [TODO: Component description here]
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} [TODO: Rendered output]
 */
function ThreeModel() {
    const containerRef = useRef();

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Scene setup
        const scene = new THREE.Scene();

        // Camera setup
        const camera = new THREE.PerspectiveCamera(
            75,
            container.clientWidth / container.clientHeight,
            0.1,
            1000
        );
        camera.position.z = 2;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        renderer.setSize(container.clientWidth, container.clientHeight);
        container.appendChild(renderer.domElement);

        // Lighting
        const light = new THREE.HemisphereLight(0xffffff, 0x444444);
        light.position.set(0, 20, 0);
        scene.add(light);

        let pivot = null;
        let animationFrameId;
        let isVisible = true; // Updated by IntersectionObserver

        // Load model
        const loader = new GLTFLoader();
        loader.load(
            "/models/3dExport.gltf",
            (gltf) => {
                const model = gltf.scene;

                // Wireframe material
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.material = new THREE.MeshBasicMaterial({
                            color: 0xdee3cb,
                            wireframe: true,
                        });
                    }
                });

                // Scale
                model.scale.set(0.01, 0.01, 0.01);

                // Pivot container
                pivot = new THREE.Object3D();

                // Center model
                const box = new THREE.Box3().setFromObject(model);
                const center = box.getCenter(new THREE.Vector3());
                model.position.sub(center);

                pivot.add(model);
                pivot.position.set(-1, -0.25, 0);
                scene.add(pivot);

                animate(); // Start animation loop after model loads
            },
            (xhr) => console.log((xhr.loaded / xhr.total) * 100 + "% loaded"),
            (error) => console.error("Error loading model:", error)
        );

        // Handle window resize
        const handleResize = () => {
            camera.aspect = container.clientWidth / container.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(container.clientWidth, container.clientHeight);
        };
        window.addEventListener("resize", handleResize);

        // Animate loop with visibility check
        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);

            if (pivot && isVisible) {
                pivot.rotation.y += 0.001;
                renderer.render(scene, camera);
            }
        };

        // IntersectionObserver to pause rendering offscreen
        const observer = new IntersectionObserver(
            ([entry]) => {
                isVisible = entry.isIntersecting;
            },
            { threshold: 0.1 }
        );
        observer.observe(container);

        // Cleanup
        return () => {
            cancelAnimationFrame(animationFrameId);
            observer.disconnect();
            window.removeEventListener("resize", handleResize);
            container.removeChild(renderer.domElement);
        };
    }, []);

    return (
        <div
            id="three-container"
            ref={containerRef}
            style={{ width: "100%", height: "100%" }}
        ></div>
    );
}

export default ThreeModel;
